import React, { useEffect, useState, useCallback } from "react";
import { FaEdit } from "react-icons/fa";
import axios from "axios";
import { Container, Button, Form, Offcanvas, Card } from "react-bootstrap";
import useAccess from "../../hooks/useAccess";
import "../../styles/WorkHistoryPage.css";
import "../../styles/JobDescriptionPage.css";
import dayjs from "dayjs";

function JobDescriptionPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [records, setRecords] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [form, setForm] = useState({ jd_title: "", jd_body: "" });

  //  Access Control
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    descriptions: {
      create: hasPermission("JobDescriptionPage", "Job Description", "create"),
      edit: hasPermission("JobDescriptionPage", "Job Description", "edit"),
      delete: hasPermission("JobDescriptionPage", "Job Description", "delete"),
    },
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/job-desc`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("❌ Fetch error", err);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrawer = (rec) => {
    setEditRec(rec);
    setForm(
      rec
        ? { jd_title: rec.jd_title, jd_body: rec.jd_body }
        : { jd_title: "", jd_body: "" }
    );
    setShowDrawer(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editRec) {
        await axios.put(`${BASE_URL}/api/job-desc/${editRec.jd_id}`, form, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      } else {
        await axios.post(`${BASE_URL}/api/job-desc`, form, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      }
      setShowDrawer(false);
      fetchData();
    } catch (err) {
      console.error("❌ Save error", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      await axios.delete(`${BASE_URL}/api/job-desc/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchData();
      alert("✅ Entry deleted successfully.");
    } catch (err) {
      console.error("❌ Delete error", err);
      alert("Failed to delete entry.");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="text-center mb-4">
        <h3>Job Description Repository</h3>
      </div>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title className="mb-0">All Job Descriptions</Card.Title>
            {PERMISSIONS.descriptions.create && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => openDrawer(null)}
              >
                Add Description
              </Button>
            )}
          </div>

          {records.length === 0 ? (
            <p className="mt-3 text-muted fst-italic">
              No job descriptions yet.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle text-center">
                <thead className="table-light">
                  <tr>
                    <th>TITLE</th>
                    <th>CREATED BY</th>
                    <th>CREATED ON</th>
                    <th>UPDATED BY</th>
                    <th>UPDATED ON</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((rec) => (
                    <tr key={rec.jd_id}>
                      <td className="text-center">{rec.jd_title}</td>
                      <td>{rec.created_by_name || "N/A"}</td>
                      <td>{dayjs(rec.created_at).format("MMM D, YYYY")}</td>
                      <td>{rec.updated_by_name || "N/A"}</td>
                      <td>{dayjs(rec.updated_at).format("MMM D, YYYY")}</td>
                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          {/* ✏️ Edit button — shown only if user has edit permission */}
                          <button
                            className="btn btn-warning btn-sm btn-action"
                            onClick={() => openDrawer(rec)}
                            disabled={!PERMISSIONS.descriptions.edit}
                          >
                            <FaEdit />
                          </button>

                          {/* 🗑️ Delete button — requires delete permission and not self-delete */}
                          {PERMISSIONS.descriptions.delete && (
                            <button
                              className="btn btn-danger btn-sm btn-action"
                              onClick={() => handleDelete(rec.jd_id)}
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Offcanvas
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        placement="start"
        className="offcanvas-wide"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {editRec ? "Edit Description" : "Add New Description"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                name="jd_title"
                value={form.jd_title}
                onChange={(e) => setForm({ ...form, jd_title: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description Body</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                name="jd_body"
                value={form.jd_body}
                onChange={(e) => setForm({ ...form, jd_body: e.target.value })}
              />
            </Form.Group>
            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={() => setShowDrawer(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}

export default JobDescriptionPage;
